import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { createConnectAccountLink, getMyStripeAccount } from "@/lib/stripe.functions";

export const Route = createFileRoute("/_authenticated/creator-payouts")({
  head: () => ({ meta: [{ title: "Payouts — Pluggz" }, { name: "robots", content: "noindex" }] }),
  component: CreatorPayouts,
});

function CreatorPayouts() {
  const { user } = useAuth();
  const startConnect = useServerFn(createConnectAccountLink);
  const fetchAccount = useServerFn(getMyStripeAccount);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const account = useQuery({
    queryKey: ["my-stripe-account", user?.id],
    queryFn: () => fetchAccount({ data: undefined } as never),
    enabled: !!user,
  });

  const payouts = useQuery({
    queryKey: ["my-payouts", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: creator } = await supabase.from("creators").select("id").eq("user_id", user!.id).maybeSingle();
      if (!creator) return [];
      const { data } = await supabase.from("payouts").select("*").eq("creator_id", creator.id).order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  async function connect() {
    setBusy(true); setError(null);
    const res = await startConnect({ data: undefined } as never);
    setBusy(false);
    if (!res.ok) return setError(res.error ?? "Could not start Stripe onboarding.");
    window.location.href = res.url!;
  }

  const acct = account.data?.account;
  const stripeReady = account.data?.stripeConfigured;

  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <Link to="/creator-dashboard" className="text-sm text-muted-foreground">← Dashboard</Link>
      <h1 className="mt-4 font-serif text-4xl">Payouts</h1>
      <p className="mt-2 text-muted-foreground">Connect your bank with Stripe to receive commission payouts.</p>

      <div className="mt-8 border border-border bg-card p-6">
        {!stripeReady ? (
          <p className="text-sm text-muted-foreground">Payouts will be enabled once an admin adds Stripe credentials. Hang tight.</p>
        ) : !acct ? (
          <>
            <p className="text-sm">You haven't connected a Stripe account yet.</p>
            <button onClick={connect} disabled={busy} className="mt-4 bg-foreground px-6 py-3 text-xs uppercase tracking-widest text-background disabled:opacity-50">
              {busy ? "Opening…" : "Connect with Stripe"}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm">Stripe account: <code className="text-xs">{acct.stripe_account_id}</code></p>
            <ul className="mt-2 text-xs text-muted-foreground space-y-1">
              <li>Details submitted: {acct.details_submitted ? "yes" : "no"}</li>
              <li>Charges enabled: {acct.charges_enabled ? "yes" : "no"}</li>
              <li>Payouts enabled: {acct.payouts_enabled ? "yes" : "no"}</li>
            </ul>
            {(!acct.payouts_enabled || !acct.details_submitted) && (
              <button onClick={connect} disabled={busy} className="mt-4 border border-border px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50">
                {busy ? "Opening…" : "Continue onboarding"}
              </button>
            )}
          </>
        )}
        {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
      </div>

      <h2 className="mt-12 font-serif text-2xl">History</h2>
      <div className="mt-4 border border-border">
        {(payouts.data ?? []).length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No payouts yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Status</th></tr>
            </thead>
            <tbody>
              {payouts.data!.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-4 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{(p.amount_cents / 100).toFixed(2)} {p.currency?.toUpperCase()}</td>
                  <td className="px-4 py-3">{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}